// One journal, at /journals/:id — the cover hero and what has been gathered into it.
//
// Ported from journals-web.jsx (CollectionDetail). The rows are the feed's rows, not copies of them:
// a journal is a way of looking at entries, so an entry has to look the same wherever it is seen.

import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useEntries } from '../entries/EntriesProvider';
import { useJournals } from './JournalsProvider';
import { EntryRow, entryLabel } from '../entries/EntryRow';
import { AddEntriesSheet } from './AddEntriesSheet';
import { EditJournalSheet } from './EditJournalSheet';
import { coverById } from './covers';
import { JournalToast, useToast } from './toast';
import { ConfirmDialog, type ConfirmSpec } from '../components/SettingsKit';
import { Notice, Spinner } from '../components/ui';
import {
  IconChevronLeft,
  IconClose,
  IconFolder,
  IconPalette,
  IconPen,
  IconPlus,
  IconTrash,
} from '../components/Icons';

export function JournalDetailScreen() {
  const { journalId = '' } = useParams();
  const navigate = useNavigate();
  const { entries } = useEntries();
  const journals = useJournals();
  const collection = journals.byId(journalId);

  const [editing, setEditing] = useState(false);
  const [adding, setAdding] = useState(false);
  const [dressing, setDressing] = useState(false);
  const [confirm, setConfirm] = useState<ConfirmSpec | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { toast, ping } = useToast();

  // Membership order, resolved against the feed. An id with no entry behind it is one the feed has
  // not loaded rather than one that is gone, so it is simply skipped rather than shown as a gap.
  const items = useMemo(() => {
    if (!collection) return [];
    const byId = new Map(entries.map((entry) => [entry.id, entry]));
    return collection.entryIds.map((id) => byId.get(id)).filter((e) => e !== undefined);
  }, [collection, entries]);

  // A draft is not something to file: it is not finished, and it is kept out of every other list.
  const collectable = useMemo(() => entries.filter((entry) => !entry.isDraft), [entries]);

  if (journals.isLoading) return <Spinner label="Opening your journal…" />;

  if (!collection) {
    return (
      <>
        <div className="jpage-head">
          <button className="jback" onClick={() => navigate('/journals')} aria-label="Back to journals">
            <IconChevronLeft />
          </button>
          <span className="ttl">Journal</span>
        </div>
        <div className="coll-empty">This journal is no longer on your shelf.</div>
      </>
    );
  }

  const design = coverById(collection.coverId);

  const guard = (work: Promise<unknown>, done: string) => {
    setError(null);
    work.then(
      () => ping(done),
      (err: unknown) => setError(err instanceof Error ? err.message : 'That did not save.'),
    );
  };

  const askDeleteJournal = () =>
    setConfirm({
      title: 'Delete this journal?',
      body: (
        <>
          <strong>{collection.title || 'This journal'}</strong> is removed from your shelf. The{' '}
          {items.length === 1 ? 'entry' : `${items.length} entries`} inside{' '}
          {items.length === 1 ? 'stays' : 'stay'} in your feed and in any other journal.
        </>
      ),
      confirmLabel: 'Delete journal',
      danger: true,
      onConfirm: async () => {
        await journals.remove(collection.id);
        navigate('/journals', { replace: true });
      },
    });

  return (
    <>
      <div className="jpage-head">
        <button className="jback" onClick={() => navigate('/journals')} aria-label="Back to journals">
          <IconChevronLeft />
        </button>
        <span className="crumb">Journals / {collection.title || 'Untitled'}</span>
      </div>

      {error ? <Notice tone="danger">{error}</Notice> : null}

      <div className="wsplit">
        <div className="coll-hero" style={{ background: design.bg }}>
          <span className={`coll-hero-veil ink-${design.ink}`} />
          <div className={`coll-hero-txt ink-${design.ink}`}>
            <span className="ck">Journal</span>
            <h1>{collection.title || 'Untitled journal'}</h1>
            <span className="cm">
              {items.length} {items.length === 1 ? 'entry' : 'entries'}
              {collection.subtitle ? ` · ${collection.subtitle}` : ''}
            </span>
          </div>
          <button
            className={`coll-editcover ink-${design.ink}`}
            onClick={() => setDressing(true)}
          >
            <IconPalette /> Edit cover
          </button>
        </div>

        <div className="coll-scroll">
          <div className="coll-actions">
            <button className="cbtn primary" onClick={() => setAdding(true)}>
              <IconPlus /> Add entries
            </button>
            <button
              className="cbtn"
              onClick={() => navigate(`/write?journal=${encodeURIComponent(collection.id)}`)}
            >
              <IconPen /> Write here
            </button>
            <button
              className={`cbtn ${editing ? 'on' : ''}`}
              onClick={() => setEditing((v) => !v)}
              aria-pressed={editing}
            >
              {editing ? 'Done' : 'Edit'}
            </button>
            <button className="cbtn danger" onClick={askDeleteJournal}>
              <IconTrash /> Delete
            </button>
          </div>

          {items.map((entry) => {
            const elsewhere = journals.countFor(entry.id);
            return (
              <EntryRow
                key={entry.id}
                entry={entry}
                onOpen={() => navigate(`/write/${entry.id}`)}
                trailing={
                  editing ? (
                    <button
                      className="crm"
                      onClick={() =>
                        guard(
                          journals.removeEntry(collection.id, entry.id),
                          'Removed from this journal — entry kept',
                        )
                      }
                      aria-label={`Remove ${entryLabel(entry)} from this journal`}
                    >
                      <IconClose />
                    </button>
                  ) : elsewhere > 1 ? (
                    <span className="cin" title={`In ${elsewhere} journals`}>
                      <IconFolder />
                      {elsewhere}
                    </span>
                  ) : null
                }
              />
            );
          })}

          {items.length === 0 ? (
            <div className="coll-empty">
              Nothing collected here yet. Add existing entries, or write a new one straight into
              this journal.
            </div>
          ) : null}

          {editing ? (
            <p className="coll-hint">
              Removing takes an entry out of this journal only. It stays in your feed and in any
              other journal.
            </p>
          ) : null}
        </div>
      </div>

      {adding ? (
        <AddEntriesSheet
          collection={collection}
          entries={collectable}
          countFor={journals.countFor}
          onClose={() => setAdding(false)}
          onCommit={(ids) => {
            setAdding(false);
            guard(journals.setEntries(collection.id, ids), 'Journal updated');
          }}
        />
      ) : null}

      {dressing ? (
        <EditJournalSheet
          collection={collection}
          onClose={() => setDressing(false)}
          onSave={(edits) => {
            setDressing(false);
            guard(journals.update(collection.id, edits), 'Journal updated');
          }}
        />
      ) : null}

      <ConfirmDialog spec={confirm} onClose={() => setConfirm(null)} />
      <JournalToast text={toast} />

      <div style={{ height: 60 }} />
    </>
  );
}
